import React, { useState, useEffect, useRef } from 'react';
import { MapPin, ChevronRight } from 'lucide-react';
import { useMap } from 'react-leaflet';
import { createPortal } from 'react-dom';
import './MapLocation.css';

const MapLocation = ({ onLocationSelect, selectedLocation, popupOpen, onMenuStateChange }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showCountiesSubmenu, setShowCountiesSubmenu] = useState(false);
  const [showCitiesSubmenu, setShowCitiesSubmenu] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const countiesMenuRef = useRef(null);
  const citiesMenuRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.map-location-menu') && 
          !event.target.closest('.map-location-button') &&
          !event.target.closest('.counties-submenu') &&
          !event.target.closest('.cities-submenu')) {
        setShowMenu(false);
        setShowCountiesSubmenu(false);
        setShowCitiesSubmenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, []);

  useEffect(() => {
    if (popupOpen) {
      setShowMenu(false);
      setShowCountiesSubmenu(false);
      setShowCitiesSubmenu(false);
    }
  }, [popupOpen]);

  useEffect(() => {
    const isAnyMenuOpen = showMenu || showCountiesSubmenu || showCitiesSubmenu;
    if (onMenuStateChange) {
      onMenuStateChange(isAnyMenuOpen);
    }
  }, [showMenu, showCountiesSubmenu, showCitiesSubmenu, onMenuStateChange]);

  const map = useMap();

  const handleMouseEnter = () => {
    map.doubleClickZoom.disable();
  };

  const handleMouseLeave = () => {
    map.doubleClickZoom.enable();
  };

  const mapLocations = [
    { name: 'United States', type: 'nation', label: 'Nation' },
    { name: 'All Regions', type: 'region', label: 'Regions' },
    { name: 'All Divisions', type: 'division', label: 'Divisions' },
    { name: 'All States', type: 'state', label: 'States' },
    { name: 'All Metros', type: 'metro', label: 'Metros' },
    { name: 'Counties', type: 'county', label: 'Counties', hasSubmenu: true },
    { name: 'Cities', type: 'city', label: 'Cities', hasSubmenu: true }
  ];

  const states = [
    { name: 'Alabama', abbr: 'AL' },
    { name: 'Alaska', abbr: 'AK' },
    { name: 'Arizona', abbr: 'AZ' },
    { name: 'Arkansas', abbr: 'AR' },
    { name: 'California', abbr: 'CA' },
    { name: 'Colorado', abbr: 'CO' },
    { name: 'Connecticut', abbr: 'CT' },
    { name: 'Delaware', abbr: 'DE' },
    { name: 'Florida', abbr: 'FL' },
    { name: 'Georgia', abbr: 'GA' },
    { name: 'Hawaii', abbr: 'HI' },
    { name: 'Idaho', abbr: 'ID' },
    { name: 'Illinois', abbr: 'IL' },
    { name: 'Indiana', abbr: 'IN' },
    { name: 'Iowa', abbr: 'IA' },
    { name: 'Kansas', abbr: 'KS' },
    { name: 'Kentucky', abbr: 'KY' },
    { name: 'Louisiana', abbr: 'LA' },
    { name: 'Maine', abbr: 'ME' },
    { name: 'Maryland', abbr: 'MD' },
    { name: 'Massachusetts', abbr: 'MA' },
    { name: 'Michigan', abbr: 'MI' },
    { name: 'Minnesota', abbr: 'MN' },
    { name: 'Mississippi', abbr: 'MS' },
    { name: 'Missouri', abbr: 'MO' },
    { name: 'Montana', abbr: 'MT' },
    { name: 'Nebraska', abbr: 'NE' },
    { name: 'Nevada', abbr: 'NV' },
    { name: 'New Hampshire', abbr: 'NH' },
    { name: 'New Jersey', abbr: 'NJ' },
    { name: 'New Mexico', abbr: 'NM' },
    { name: 'New York', abbr: 'NY' },
    { name: 'North Carolina', abbr: 'NC' },
    { name: 'North Dakota', abbr: 'ND' },
    { name: 'Ohio', abbr: 'OH' },
    { name: 'Oklahoma', abbr: 'OK' },
    { name: 'Oregon', abbr: 'OR' },
    { name: 'Pennsylvania', abbr: 'PA' },
    { name: 'Rhode Island', abbr: 'RI' },
    { name: 'South Carolina', abbr: 'SC' },
    { name: 'South Dakota', abbr: 'SD' },
    { name: 'Tennessee', abbr: 'TN' },
    { name: 'Texas', abbr: 'TX' },
    { name: 'Utah', abbr: 'UT' },
    { name: 'Vermont', abbr: 'VT' },
    { name: 'Virginia', abbr: 'VA' },
    { name: 'Washington', abbr: 'WA' },
    { name: 'West Virginia', abbr: 'WV' },
    { name: 'Wisconsin', abbr: 'WI' },
    { name: 'Wyoming', abbr: 'WY' }
  ];

  const CountiesSubmenu = () => {
    if (!showCountiesSubmenu || !menuRef.current) return null;
    
    const menuRect = menuRef.current.getBoundingClientRect();
    
    return (
      <div
        ref={countiesMenuRef}
        className="map-location-menu counties-submenu"
        style={{
          left: `${menuRect.right + 2}px`,
          bottom: `${window.innerHeight - menuRect.bottom}px`,
          position: 'fixed',
          maxHeight: '330px',
          overflowY: 'auto'
        }}
      >
        {states.map((state) => {
          const isSelected = selectedLocation?.info?.type === 'county' && 
                            selectedLocation?.info?.state === state.name && 
                            !selectedLocation?.info?.name?.includes(',');
          
          const countyInStateSelected = selectedLocation?.info?.type === 'county' && 
                                       (selectedLocation?.info?.state === state.name || 
                                        (selectedLocation?.info?.name?.includes(',') && 
                                         selectedLocation?.info?.name?.split(',')[1]?.trim() === state.name));
          
          return (
            <button
              key={state.name}
              className={`map-location-item 
                ${isSelected ? 'selected' : ''} 
                ${countyInStateSelected && !isSelected ? 'has-selection' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                
                if (!isSelected) {
                  onLocationSelect({
                    name: `All ${state.abbr} Counties`,
                    type: 'county',
                    info: {
                      name: `All ${state.abbr} Counties`,
                      type: 'county',
                      state: state.name,
                      stateAbbr: state.abbr
                    }
                  });
                  setShowMenu(false);
                  setShowCountiesSubmenu(false);
                }
              }}
              disabled={isSelected}
            >
              {state.name}
            </button>
          );
        })}
      </div>
    );
  };

  const CitiesSubmenu = () => {
    if (!showCitiesSubmenu || !menuRef.current) return null;
    
    const menuRect = menuRef.current.getBoundingClientRect();
    
    return (
      <div
        ref={citiesMenuRef}
        className="map-location-menu cities-submenu"
        style={{
          left: `${menuRect.right + 2}px`,
          bottom: `${window.innerHeight - menuRect.bottom}px`,
          position: 'fixed',
          maxHeight: '330px',
          overflowY: 'auto'
        }}
      >
        {states.map((state) => {
          const isSelected = selectedLocation?.info?.type === 'city' && 
                            selectedLocation?.info?.state === state.name && 
                            !selectedLocation?.info?.name?.includes(',');
          
          const cityInStateSelected = selectedLocation?.info?.type === 'city' && 
                                     (selectedLocation?.info?.state === state.name || 
                                      (selectedLocation?.info?.name?.includes(',') && 
                                       selectedLocation?.info?.name?.split(',')[1]?.trim() === state.name));
          
          return (
            <button
              key={state.name}
              className={`map-location-item 
                ${isSelected ? 'selected' : ''} 
                ${cityInStateSelected && !isSelected ? 'has-selection' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                
                if (!isSelected) {
                  onLocationSelect({
                    name: `All ${state.abbr} Cities`,
                    type: 'city',
                    info: {
                      name: `All ${state.abbr} Cities`,
                      type: 'city',
                      state: state.name,
                      stateAbbr: state.abbr
                    }
                  });
                  setShowMenu(false);
                  setShowCitiesSubmenu(false);
                }
              }}
              disabled={isSelected}
            >
              {state.name}
            </button>
          );
        })}
      </div>
    );
  };

  const MenuComponent = () => {
    if (!showMenu) return null;
    
    const buttonRect = buttonRef.current.getBoundingClientRect();
    
    return (
      <div
        ref={menuRef}
        className="map-location-menu"
        style={{
          left: `${buttonRect.right + 2}px`,
          bottom: `${window.innerHeight - buttonRect.bottom}px`,
          position: 'fixed',
          maxHeight: '330px',
          overflowY: 'auto'
        }}
      >
        {mapLocations.map((location) => {
          const isSelected = selectedLocation?.info?.type === location.type && location.type !== 'county' && location.type !== 'city';

          const countiesSelected = selectedLocation?.info?.type === 'county' && 
                                    (selectedLocation?.info?.name?.includes('All Counties') || 
                                    selectedLocation?.info?.name?.includes(','));
          
          const stateCountiesSelected = selectedLocation?.info?.type === 'county' && 
                                        selectedLocation?.info?.state;
          
          const citiesSelected = selectedLocation?.info?.type === 'city' && 
                                (selectedLocation?.info?.name?.includes('All Cities') || 
                                selectedLocation?.info?.name?.includes(','));
          
          const stateCitiesSelected = selectedLocation?.info?.type === 'city' && 
                                      selectedLocation?.info?.state;
          
          if (location.hasSubmenu) {
            return (
              <div
                key={location.name}
                className="map-location-item-container"
              >
                <button
                  className={`map-location-item with-submenu 
                    ${isSelected ? 'selected' : ''} 
                    ${(countiesSelected && location.type === 'county') || (citiesSelected && location.type === 'city') ? 'has-selection' : ''} 
                    ${(stateCountiesSelected && location.type === 'county') || (stateCitiesSelected && location.type === 'city') ? 'has-selection' : ''} 
                    ${(showCountiesSubmenu && location.type === 'county') || (showCitiesSubmenu && location.type === 'city') ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (location.type === 'county') {
                      setShowCountiesSubmenu(!showCountiesSubmenu);
                      setShowCitiesSubmenu(false);
                    } else if (location.type === 'city') {
                      setShowCitiesSubmenu(!showCitiesSubmenu);
                      setShowCountiesSubmenu(false);
                    }
                  }}
                >
                  <span>{location.label}</span>
                  <ChevronRight size={16} className="submenu-icon" />
                </button>
                {showCountiesSubmenu && location.type === 'county' && <CountiesSubmenu />}
                {showCitiesSubmenu && location.type === 'city' && <CitiesSubmenu />}
              </div>
            );
          }
          
          return (
            <button
              key={location.name}
              className={`map-location-item ${isSelected ? 'selected' : ''}`}
              onClick={() => {
                if (!isSelected) {
                  onLocationSelect({
                    name: location.name,
                    type: location.type,
                    info: {
                      name: location.name,
                      type: location.type
                    }
                  });
                  setShowMenu(false);
                  setShowCountiesSubmenu(false);
                  setShowCitiesSubmenu(false);
                }
              }}
              disabled={isSelected}
            >
              {location.label}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="leaflet-bottom leaflet-left map-location">
      <div 
        className="leaflet-control"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button 
          ref={buttonRef}
          className={`map-location-button ${showMenu ? 'active' : ''}`}
          onClick={() => {
            const newShowMenu = !showMenu;
            setShowMenu(newShowMenu);
            if (!newShowMenu) {
              setShowCountiesSubmenu(false);
              setShowCitiesSubmenu(false);
            }
          }}
        >
          <MapPin size={20} className="map-location-icon" />
        </button>
        
        {showMenu && createPortal(
          <MenuComponent />,
          document.body
        )}
      </div>
    </div>
  );
};

export default MapLocation;